import { useEffect, useState } from "react";

interface ImageMetadata {
  poster: { source: string; tmdbUrl: string | null; r2Url: string | null };
  backdrop: { source: string; tmdbUrl: string | null; r2Url: string | null };
  logo: { source: string; tmdbUrl: string | null; r2Url: string | null };
}

interface ImageApiResponse {
  title: { id: string; name: string; slug: string };
  images: {
    poster: {
      current: string | null;
      source: string;
      tmdbUrl: string | null;
      r2Url: string | null;
      canToggle: boolean;
    };
    backdrop: {
      current: string | null;
      source: string;
      tmdbUrl: string | null;
      r2Url: string | null;
      canToggle: boolean;
    };
    logo: {
      current: string | null;
      source: string;
      tmdbUrl: string | null;
      r2Url: string | null;
      canToggle: boolean;
    };
    tiles: {
      horizontal: string | null;
      vertical: string | null;
    };
  };
}

/**
 * Hook to fetch and manage image metadata for titles
 */
export function useImageMetadata(titleId: string | null, mode: "submission" | "title") {
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchMetadata = async (): Promise<ImageApiResponse | undefined> => {
    if (mode !== "title" || !titleId) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("lumi-access-token");
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/admin/titles/${titleId}/images`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as ImageApiResponse;
        console.log("Image metadata fetched:", data);
        setImageMetadata({
          poster: {
            source: data.images.poster.source,
            tmdbUrl: data.images.poster.tmdbUrl,
            r2Url: data.images.poster.r2Url,
          },
          backdrop: {
            source: data.images.backdrop.source,
            tmdbUrl: data.images.backdrop.tmdbUrl,
            r2Url: data.images.backdrop.r2Url,
          },
          logo: {
            source: data.images.logo.source,
            tmdbUrl: data.images.logo.tmdbUrl,
            r2Url: data.images.logo.r2Url,
          },
        });
        return data;
      }
      console.error("Failed to fetch image metadata:", response.status, response.statusText);
    } catch (error) {
      console.error("Failed to fetch image metadata:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch metadata on mount
  useEffect(() => {
    void fetchMetadata();
  }, [mode, titleId]);

  return {
    imageMetadata,
    loading,
    refetch: fetchMetadata,
  };
}
